import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  isApp,
  tcGetDeviceType,
  tcGetAppVersion,
  tcGetAppChannel,
  tcGetDeviceId,
} from '@/utils/bridge'
import { gteVersion } from '@/utils/version'
import { getSearchParam } from '@/utils/urlQuery'

/** 启用新头部 / Tab 保活的最低 App 版本号 */
export const NEW_HEADER_MIN_APP_VERSION = '1.2.0'

/** 新结构（3 Tab）配置 */
export interface NewTabItem {
  title: string
  path: string
  /** iconfont 类名，可选 */
  icon?: string
}

/** 旧结构（4 Tab）配置 */
export interface LegacyTabItem {
  title: string
  path: string
}

/**
 * 获取来源类型
 * App 环境：返回 ''
 * H5 环境：从 URL 参数 ?source=xxx 获取
 */
function getSourceType(): string {
  try {
    // App 环境直接返回 ''
    if (isApp()) {
      return ''
    }

    // H5 环境：从 URL 参数获取 source
    const urlSource = getSearchParam('source')

    if (urlSource) {
      return urlSource.toLowerCase()
    }

    return ''
  } catch (error) {
    console.error('[AppStore] 获取 sourceType 失败:', error)
    return ''
  }
}

/**
 * 设备信息接口
 */
export interface DeviceInfo {
  deviceType: string      // iOS/Android/HarmonyOS
  clientVersion: string   // 应用版本号 如 1.0.0
  appChannel: string      // 渠道信息 如 appstore/vivo/huawei
  deviceId: string        // 设备唯一标识 UUID
  sourceType: string      // 来源类型
}

/**
 * App Store
 * 用于管理 App 环境专有的数据（设备信息等）
 *
 * 设计目标：
 * 1. 在应用启动时一次性初始化设备信息（这些信息不会变）
 * 2. 提供同步访问接口，避免频繁的 JSBridge 调用
 * 3. 统一管理 App 环境的特殊逻辑
 *
 * 重要说明：
 * - 设备信息（deviceType、appVersion 等）是静态的，可以缓存
 * - Token 不应该缓存！因为 Native 侧可能随时更新，必须每次从 Native 获取
 */
export const useAppStore = defineStore('app', () => {
  // ==================== 状态 ====================

  /**
   * 设备信息（仅 App 环境有值）
   * 这些信息在应用生命周期内不会变化，可以安全缓存
   */
  const deviceInfo = ref<DeviceInfo | null>(null)

  /**
   * 初始化状态标记
   */
  const isInitialized = ref(false)

  /**
   * 删除账单确认弹框是否正在显示
   */
  const isDeleteDialogShowing = ref(false)

  /**
   * App 好评弹窗显示状态（提升到 store 以便多个 HomeLayout 实例共享）
   */
  const showAppRatingDialog = ref(false)
  const ratingExposureId = ref<string | null>(null)
  const ratingTriggerType = ref<string | null>(null)

  // ==================== Tab 灰度相关 ====================

  /**
   * 是否启用新头部 / Tab 保活
   * - H5 环境直接启用
   * - App 环境：版本号 >= NEW_HEADER_MIN_APP_VERSION 启用，低版本兜底旧结构
   */
  const enableNewHeader = computed<boolean>(() => {
    if (!isApp()) return true
    const version = deviceInfo.value?.clientVersion || ''
    if (!version) return false
    return gteVersion(version, NEW_HEADER_MIN_APP_VERSION)
  })

  /**
   * KeepAlive include 名单（与新结构 3 个 Tab 子页面 defineOptions name 对齐）
   */
  const keepAliveIncludes = computed<string[]>(() => {
    return enableNewHeader.value ? ['BillView', 'DetailsView', 'ChatView'] : []
  })

  // FR-09 派生：快捷录入 / 语音 / 待确定区灰度守门，复用 enableNewHeader 单一真相源
  const enableQuickInput = computed<boolean>(() => enableNewHeader.value)
  const enableVoiceInput = computed<boolean>(() => enableNewHeader.value)
  const enableDraftZone = computed<boolean>(() => enableNewHeader.value)

  /**
   * Tab 配置（新结构 3 项 / 旧结构 4 项）
   */
  const tabConfig = computed<NewTabItem[] | LegacyTabItem[]>(() => {
    if (enableNewHeader.value) {
      // icon 字段为 iconfont 类名（不含 'iconfont' 前缀）；
      // 待设计师确认/补图标后填入对应类名，留空时仅渲染文字
      return [
        { title: '账单', path: '/bill', icon: '' },
        { title: '明细', path: '/details', icon: '' },
        { title: '智能助手', path: '/chat', icon: '' },
      ] as NewTabItem[]
    }
    return [
      { title: '账单', path: '/bill' },
      { title: '明细', path: '/details' },
      { title: '智能助手', path: '/chat' },
      { title: '我的', path: '/mine' },
    ] as LegacyTabItem[]
  })

  // ==================== Actions ====================

  /**
   * 初始化设备信息
   * 应该在应用启动时调用一次，并发获取所有设备信息
   */
  const initDeviceInfo = async (): Promise<void> => {
    if (!isApp() || deviceInfo.value) {
      return
    }

    try {
      const [deviceType, clientVersion, appChannel, deviceId] = await Promise.all([
        tcGetDeviceType(),
        tcGetAppVersion(),
        tcGetAppChannel(),
        tcGetDeviceId(),
      ])

      deviceInfo.value = {
        deviceType,
        clientVersion,
        appChannel,
        deviceId,
        sourceType: getSourceType(),
      }
    } catch (error) {
      console.error('[AppStore] 初始化设备信息失败:', error)
      deviceInfo.value = {
        deviceType: '',
        clientVersion: '',
        appChannel: '',
        deviceId: '',
        sourceType: '',
      }
    }
  }

  /**
   * 初始化所有 App 数据
   * 在应用启动时调用，只初始化设备信息（不包括 Token）
   */
  const initAllAppData = async (): Promise<void> => {
    if (!isApp()) {
      return
    }

    if (isInitialized.value) {
      return
    }

    try {
      await initDeviceInfo()
      isInitialized.value = true
    } catch (error) {
      console.error('[AppStore] 初始化 App 数据失败:', error)
      isInitialized.value = true
    }
  }

  /**
   * 获取设备信息（同步）
   */
  const getDeviceInfo = (): DeviceInfo | null => {
    return deviceInfo.value
  }

  return {
    // 状态
    deviceInfo,
    isInitialized,
    isDeleteDialogShowing,
    showAppRatingDialog,
    ratingExposureId,
    ratingTriggerType,

    // 灰度相关
    enableNewHeader,
    keepAliveIncludes,
    tabConfig,
    enableQuickInput,
    enableVoiceInput,
    enableDraftZone,

    // Actions
    initDeviceInfo,
    initAllAppData,
    getDeviceInfo,
  }
})
